'use client';

// Detail view for a single managed-agent run. Shows the auditable ledger record
// (what fired it, outcome + summary/error, steps, credits, duration, config
// version) AND — new in v2.1 — THIS run's own captured message window, lazily
// fetched from GET /api/agents/:id/runs/:runId/transcript. The agent's full
// conversation still lives in its shared session (which only snapshots the
// LATEST turn), so the "Open full session" link stays; the per-run transcript is
// the specific input + output THIS run produced. Runs that predate the linkage
// (or captured nothing) render a clean fallback.

import { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  ArrowSquareOut,
  Circle,
  FileText,
  Flag,
  WarningCircle,
  Coins,
  Spinner,
  Clock,
  Wrench,
  Flask,
  User,
  Robot,
  CaretRight,
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { describeToolPart } from '@/lib/timeline-synthesis';
import { readToolPartInput } from '@/lib/tool-part-input';
import { stripSystemTime } from '@/lib/system-time-tag';
import { parseTurnError } from '@/lib/turn-error';
import type { AgentRun, RunOutcome, RunTranscript, RunTranscriptMessage } from './types';
import { relativeTime } from './relative-time';

function OutcomeIcon({ outcome, status }: { outcome: RunOutcome | null; status: string }) {
  if (status === 'running') return <Spinner weight="bold" className="size-4 animate-spin text-violet-400" />;
  switch (outcome) {
    case 'report':
      return <FileText weight="duotone" className="size-4 text-sky-400" />;
    case 'proposal':
      return <Flag weight="duotone" className="size-4 text-violet-400" />;
    case 'error':
      return <WarningCircle weight="duotone" className="size-4 text-rose-400" />;
    case 'budget_skip':
      return <Coins weight="duotone" className="size-4 text-amber-400" />;
    default:
      return <Circle weight="fill" className="size-2.5 text-muted-foreground/50" />;
  }
}

function outcomeTitle(run: AgentRun): string {
  if (run.status === 'running') return 'Running…';
  if (run.status === 'failed') return 'Run failed';
  switch (run.outcome) {
    case 'report':
      return 'Report';
    case 'proposal':
      return 'Proposal awaiting review';
    case 'error':
      return 'Error';
    case 'budget_skip':
      return 'Skipped — daily budget reached';
    case 'heartbeat_ok':
      return 'All quiet — nothing to report';
    default:
      return 'Completed';
  }
}

function formatDuration(startedAt: string, finishedAt: string | null): string {
  if (!finishedAt) return '—';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border/50 bg-background/40 px-2.5 py-2">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">{label}</p>
      <p className="mt-0.5 text-[13px] font-semibold text-foreground/90">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read-only per-run transcript render. Deliberately NOT a full chat renderer —
// it reuses timeline-synthesis phrasing for tool calls (same voice as the
// build-timeline) and shows plain user/assistant text. Base64 is already
// stripped server-side; artifacts live behind URLs/ids in the tool parts.
// ---------------------------------------------------------------------------

interface RenderedMessage {
  key: string;
  role: string;
  texts: string[];
  errorTexts: string[];
  toolPhrases: Array<{ phrase: string; errored: boolean }>;
}

function renderMessage(msg: RunTranscriptMessage, index: number): RenderedMessage | null {
  const texts: string[] = [];
  const errorTexts: string[] = [];
  const toolPhrases: Array<{ phrase: string; errored: boolean }> = [];

  for (const part of msg.parts) {
    const type = typeof part.type === 'string' ? part.type : '';
    if (type === 'text' && typeof part.text === 'string') {
      const parsedError = parseTurnError(part.text);
      if (parsedError) {
        if (parsedError.message) errorTexts.push(parsedError.message);
        continue;
      }
      const clean = stripSystemTime(part.text).trim();
      if (clean) texts.push(clean);
    } else if (type.startsWith('tool-')) {
      const state = typeof part.state === 'string' ? part.state : '';
      if (state !== 'output-available' && state !== 'output-error') continue;
      toolPhrases.push({
        phrase: describeToolPart({
          toolName: type.replace('tool-', ''),
          // rawInput-aware: an output-error part has no parsed `input`.
          input: readToolPartInput(part),
          output: part.output,
          errored: state === 'output-error',
        }),
        errored: state === 'output-error',
      });
    }
  }

  if (texts.length === 0 && errorTexts.length === 0 && toolPhrases.length === 0) return null;
  return {
    key: msg.id || `${msg.role}-${index}`,
    role: msg.role,
    texts,
    errorTexts,
    toolPhrases,
  };
}

function RunTranscriptView({ transcript }: { transcript: RunTranscript }) {
  const rendered = transcript.messages
    .map((m, i) => renderMessage(m, i))
    .filter((m): m is RenderedMessage => m !== null);

  if (rendered.length === 0) {
    return (
      <p className="text-[12px] text-muted-foreground/70">
        This run captured no readable messages.
      </p>
    );
  }

  return (
    <div className="space-y-2.5">
      {rendered.map((m) => {
        const isUser = m.role === 'user';
        const Icon = isUser ? User : Robot;
        return (
          <div key={m.key} className="rounded-lg border border-border/50 bg-background/40 px-3 py-2.5">
            <p className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
              <Icon weight="duotone" className="size-3.5" />
              {isUser ? 'Input' : 'Agent'}
            </p>
            {m.texts.map((t, i) => (
              <p key={`t${i}`} className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground/90">
                {t}
              </p>
            ))}
            {m.toolPhrases.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {m.toolPhrases.map((tp, i) => (
                  <li
                    key={`w${i}`}
                    className={`flex items-center gap-1 text-[11.5px] ${tp.errored ? 'text-rose-300' : 'text-muted-foreground/80'}`}
                  >
                    <CaretRight weight="bold" className="size-3 shrink-0 text-muted-foreground/50" />
                    {tp.phrase}
                  </li>
                ))}
              </ul>
            )}
            {m.errorTexts.map((t, i) => (
              <p key={`e${i}`} className="mt-1 text-[12px] leading-relaxed text-rose-300">
                {t}
              </p>
            ))}
          </div>
        );
      })}
      {transcript.truncated && (
        <p className="text-[10.5px] italic text-muted-foreground/60">
          Transcript truncated to fit the size limit — open the full session for everything.
        </p>
      )}
    </div>
  );
}

type TranscriptState =
  | { status: 'loading' }
  | { status: 'loaded'; transcript: RunTranscript | null }
  | { status: 'error'; message: string };

function RunTranscriptSection({ agentId, runId }: { agentId: string; runId: string }) {
  const [state, setState] = useState<TranscriptState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      try {
        const res = await fetch(`/api/agents/${agentId}/runs/${runId}/transcript`);
        if (!res.ok) throw new Error(`Failed to load transcript (${res.status})`);
        const data = await res.json();
        if (!cancelled) {
          setState({ status: 'loaded', transcript: (data?.transcript ?? null) as RunTranscript | null });
        }
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'error', message: err instanceof Error ? err.message : 'Failed to load transcript' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId, runId]);

  if (state.status === 'loading') {
    return (
      <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
        <Spinner weight="bold" className="size-3.5 animate-spin" /> Loading this run's transcript…
      </p>
    );
  }
  if (state.status === 'error') {
    return <p className="text-[12px] text-rose-400">{state.message}</p>;
  }
  if (!state.transcript || state.transcript.messages.length === 0) {
    // Backfill honesty: runs created before per-run linkage (or that captured
    // nothing) have no stored window.
    return (
      <p className="text-[12px] text-muted-foreground/70">
        Transcript not captured — this run predates per-run transcript linkage.
      </p>
    );
  }
  return <RunTranscriptView transcript={state.transcript} />;
}

export function AgentRunDetail({
  run,
  sessionId,
  onOpenChange,
}: {
  run: AgentRun | null;
  sessionId: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={Boolean(run)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        {run && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <OutcomeIcon outcome={run.outcome} status={run.status} />
                {outcomeTitle(run)}
                {run.dryRun && (
                  <span className="flex items-center gap-1 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-400">
                    <Flask weight="bold" className="size-3" /> Test
                  </span>
                )}
              </DialogTitle>
              <DialogDescription>
                {run.trigger === 'cron'
                  ? 'Scheduled run'
                  : run.trigger === 'event'
                    ? 'Event-triggered run'
                    : run.dryRun
                      ? 'Manual test run — no effect on the schedule'
                      : 'Manual run'}
                {' · '}
                {relativeTime(run.startedAt)}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              {/* Summary / error */}
              {run.status === 'failed' || run.outcome === 'error' ? (
                <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 px-3 py-2.5 text-[13px] text-rose-300">
                  {run.error || run.summary || 'The run failed without a message.'}
                </div>
              ) : run.summary ? (
                <div className="rounded-lg border border-border/50 bg-background/40 px-3 py-2.5 text-[13px] leading-relaxed text-foreground/90 whitespace-pre-wrap">
                  {run.summary}
                </div>
              ) : run.status === 'running' ? (
                <p className="text-[13px] text-muted-foreground">This run is still in progress.</p>
              ) : (
                <p className="text-[13px] text-muted-foreground">No summary was recorded for this run.</p>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Stat
                  label="Steps"
                  value={
                    <span className="flex items-center gap-1">
                      <Wrench weight="duotone" className="size-3.5 text-muted-foreground" />
                      {run.steps}
                    </span>
                  }
                />
                <Stat
                  label="Credits"
                  value={
                    <span className="flex items-center gap-1">
                      <Coins weight="duotone" className="size-3.5 text-amber-400" />
                      {run.creditsSpent}
                    </span>
                  }
                />
                <Stat
                  label="Duration"
                  value={
                    <span className="flex items-center gap-1">
                      <Clock weight="duotone" className="size-3.5 text-muted-foreground" />
                      {formatDuration(run.startedAt, run.finishedAt)}
                    </span>
                  }
                />
                <Stat label="Trigger" value={<span className="capitalize">{run.trigger}</span>} />
                <Stat label="Status" value={<span className="capitalize">{run.status}</span>} />
                <Stat label="Config" value={run.configVersion != null ? `v${run.configVersion}` : '—'} />
              </div>

              <p className="text-[11px] text-muted-foreground/70">
                Started {new Date(run.startedAt).toLocaleString()}
                {run.finishedAt ? ` · finished ${new Date(run.finishedAt).toLocaleString()}` : ''}
              </p>

              {/* This run's own captured transcript (lazily fetched). */}
              <div className="space-y-1.5 border-t border-border/40 pt-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                  This run's transcript
                </p>
                <RunTranscriptSection agentId={run.agentId} runId={run.id} />
              </div>

              {/* Full-session link — the shared session shows the agent's LATEST
                  turn in full (per-run window is above). */}
              {sessionId && (
                <Link
                  href={`/chat/${sessionId}`}
                  className="flex items-center justify-center gap-1.5 rounded-full border border-border/70 px-4 py-2 text-[12px] font-semibold text-foreground/90 transition-colors hover:border-border hover:bg-muted/40"
                >
                  <ArrowSquareOut weight="bold" className="size-3.5" />
                  Open full session
                </Link>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
